import { LinkNode } from '@lexical/link'
import { $createHeadingNode, HeadingNode } from '@lexical/rich-text'
import { $unwrapNode, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $createTextNode,
  $isElementNode,
  $isParagraphNode,
  $isTextNode,
  LineBreakNode,
  type LexicalEditor,
  type LexicalNode,
  TextNode,
} from 'lexical'
import { $getEmailNode, $isEmailNode, EmailNode } from '../nodes/EmailNode'
import { $isGroupNode, GroupNode } from '../nodes/GroupNode'
import { $isLockedBlockNode, LockedBlockNode } from '../nodes/LockedBlockNode'
import { $findSlot, $isSlotNode, SlotNode } from '../nodes/SlotNode'
import { $createVariableNode, VariableNode } from '../nodes/VariableNode'
import { SAFE_URL } from '../validate/validateState'

const PLACEHOLDER = /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/
const SCHEMELESS_URL = /^[^\s:/{}]+\.[^\s{}]+$/

const $inPlainSlot = (node: LexicalNode): boolean => $findSlot(node)?.getAttrs().slotFormat === 'plain'

const $variablesInDoc = () => {
  const variables = $getEmailNode()?.getVariables() ?? []
  return {
    labels: new Map(variables.map((variable) => [variable.name, variable.label])),
    insertable: new Set(variables.filter((variable) => variable.insertable).map((variable) => variable.name)),
  }
}

const $underEmail = (node: LexicalNode): boolean => {
  const parent = node.getParent()
  return $isEmailNode(parent) || ($isGroupNode(parent) && $underEmail(parent))
}

const isBlock = (node: LexicalNode): boolean => $isSlotNode(node) || $isGroupNode(node) || $isLockedBlockNode(node)

const $cleanText = (node: TextNode): void => {
  if (node.getStyle() !== '') {
    node.setStyle('')
  }
  if (node.getFormat() !== 0 && $inPlainSlot(node)) {
    node.setFormat(0)
  }
}

const $chipFromPlaceholder = (node: TextNode): void => {
  $cleanText(node)
  if ($inPlainSlot(node) && /\s{2,}/.test(node.getTextContent())) {
    node.setTextContent(node.getTextContent().replace(/\s{2,}/g, ' '))
  }
  if (!node.isSimpleText()) {
    return
  }
  const text = node.getTextContent()
  const match = PLACEHOLDER.exec(text)
  if (!match) {
    return
  }
  const start = match.index
  const end = start + match[0].length
  const parts = start > 0 || end < text.length ? node.splitText(start, end) : [node]
  const target = start === 0 ? parts[0] : parts[1]
  const chip = $createVariableNode(match[1], $variablesInDoc().labels.get(match[1]) ?? match[1])
  chip.setFormat(target.getFormat())
  target.replace(chip)
}

const $syncChip = (node: VariableNode): void => {
  const { labels, insertable } = $variablesInDoc()
  const invalid = !insertable.has(node.getVariable())
  if (node.isInvalid() !== invalid) {
    node.setInvalid(invalid)
  }
  const label = labels.get(node.getVariable()) ?? node.getVariable()
  if (node.getTextContent() !== label) {
    node.setTextContent(label)
  }
  $cleanText(node)
}

const $foldPlainSlot = (slot: SlotNode, children: LexicalNode[]): void => {
  const clean = children.length === 1 && $isParagraphNode(children[0]) && children[0].getChildren().every($isTextNode)
  if (clean) {
    return
  }
  const paragraph = $createParagraphNode()
  children.forEach((block) => {
    const leaves = $isElementNode(block) ? block.getAllTextNodes() : $isTextNode(block) ? [block] : []
    if (leaves.length && paragraph.getChildrenSize()) {
      paragraph.append($createTextNode(' '))
    }
    leaves.forEach((leaf) => paragraph.append(leaf))
  })
  children.filter((child) => child.isAttached()).forEach((child) => child.remove())
  slot.append(paragraph)
}

const $normaliseSlot = (slot: SlotNode): void => {
  if (!$underEmail(slot)) {
    slot.remove()
    return
  }
  if (slot.getChildrenSize() === 0) {
    slot.append($createParagraphNode())
    return
  }
  const children = slot.getChildren()
  if (slot.getAttrs().slotFormat === 'plain') {
    $foldPlainSlot(slot, children)
    return
  }
  const loose = children.filter((child) => !$isElementNode(child) || child.isInline())
  if (loose.length) {
    const paragraph = $createParagraphNode()
    loose[0].insertBefore(paragraph)
    paragraph.append(...loose)
  }
}

export const registerContentRules = (editor: LexicalEditor): (() => void) =>
  mergeRegister(
    editor.registerNodeTransform(TextNode, $chipFromPlaceholder),
    editor.registerNodeTransform(VariableNode, $syncChip),
    editor.registerNodeTransform(HeadingNode, (node) => {
      if (node.getTag() !== 'h3') {
        node.replace($createHeadingNode('h3').append(...node.getChildren()))
      }
    }),
    editor.registerNodeTransform(LinkNode, (node) => {
      const url = node.getURL().trim()
      if (SAFE_URL.test(url)) {
        return
      }
      if (SCHEMELESS_URL.test(url)) {
        node.setURL(`https://${url}`)
        return
      }
      $unwrapNode(node)
    }),
    editor.registerNodeTransform(LineBreakNode, (node) => {
      if ($inPlainSlot(node)) {
        node.replace($createTextNode(' '))
      }
    }),
    editor.registerNodeTransform(SlotNode, $normaliseSlot),
    editor.registerNodeTransform(GroupNode, (node) => {
      if (!$underEmail(node)) {
        node.remove()
      }
    }),
    editor.registerNodeTransform(LockedBlockNode, (node) => {
      if (!$underEmail(node)) {
        node.remove()
      }
    }),
    editor.registerNodeTransform(EmailNode, (node) => {
      if (node.getParent()?.getKey() !== 'root') {
        node.remove()
        return
      }
      node
        .getChildren()
        .filter((child) => !isBlock(child))
        .forEach((child) => child.remove())
    }),
  )
